/**
 * The attendance projection (implementation plan FE-5; D8, D13; amendments
 * C3, C5): a PURE reducer from the last confirmed snapshot ERPat sent plus the
 * device's ordered event chain to
 *
 *  - what both clock surfaces show (state, shift start, break taps, the last
 *    closed shift, which events are pending / waiting for review), and
 *  - the BASE the next capture is frozen with (`base`), or `null` when no
 *    capture may happen.
 *
 * The walk (normative order, plan "Algorithm"):
 *  1. No confirmed snapshot (or an unreadable one — {@link isBaseSnapshot}) ⇒
 *     `no_base`. More than one open row ⇒ `ambiguous_base`. Nothing is offered
 *     either way, but the chain is still classified (pending / review, by the
 *     walk's own review rules) so the UI can list it.
 *  2. Start from the snapshot. C3: an open row whose `in_time` is null is still
 *     a valid base — only the shift start is unknown.
 *  3. Walk the chain in `deviceSeq` order:
 *     · final + accepted, receipt `received_at` > snapshot `as_of` (the
 *       snapshot does not reflect it yet) ⇒ apply it with the RECEIPT's row
 *       and revision; received ≤ as_of ⇒ the snapshot holds it: skip. Also
 *       skipped as reflected, whatever `received_at` says: an accepted event
 *       whose write the snapshot provably shows, and every accepted event
 *       before it (`snapshotShowsAccepted()` — the read may race the commit);
 *     · final + rejected ⇒ remember it; skip;
 *     · unsettled and depending (`dependsOn` / `localShiftId`) on a rejected
 *       or review-blocked event, or following one, or with no readable time ⇒
 *       review-blocked (cascade);
 *     · unsettled and HELD by the snapshot (Review Focus 1: ERPat committed
 *       it, the answer was lost, and a snapshot read since shows it): the
 *       snapshot shows this punch, or a LATER unsettled one — ERPat commits a
 *       chain in order, so it then committed this one too (`lastHeldIndex()`)
 *       ⇒ pending, NOT applied;
 *     · unsettled otherwise ⇒ apply provisionally (`basis: 'projected'`); a
 *       Time In opens a LOCAL shift identified by its own event id;
 *     · an unsettled event the state cannot take (Break In while working…) ⇒
 *       review-blocked, with everything after it — unless a held event came
 *       before it: then it is pending (its resend decides).
 *  4. `next`: Time In off shift unless already done for today (the closed
 *     shift STARTED today — so an overnight shift does not end the next day);
 *     Break Out / Time Out while working; Break In on a break.
 *  5. `base`: the walked identity + `dependsOn` = the last unsettled event
 *     (D8; with a base, every unsettled event is pending and none is held).
 *
 * `pending` lists every unsettled event waiting for ERPat that is not waiting
 * for review: those applied provisionally AND those NOT applied — held by the
 * snapshot, or unplaceable after a held one (the type's "applied" comment
 * predates this). `basis` is `projected` only when one was applied, so a
 * `confirmed` basis with pending events means none of them changed the state
 * shown: ERPat's own data (and so the confirmed display) may already show
 * those punches.
 *
 * Where the plan is silent, the reducer refuses to invent a base ("If no
 * validated base is available, ask for reconnection rather than inventing
 * one" — spec §4):
 *  - a self-contradicting snapshot (an open row without its id, a break count
 *    that disagrees with on/off break, an unreadable time…) ⇒ `no_base`;
 *  - a settlement the chain cannot explain — an event accepted AFTER one that
 *    is still unsettled, an accepted receipt that does not fit the state or
 *    names another row, a final row with no outcome, an accepted event newer
 *    than the snapshot whose receipt is missing ⇒ `ambiguous_base` (the next
 *    snapshot / resend clears it);
 *  - an unsettled event whose time cannot be read ⇒ review-blocked (it cannot
 *    be placed on the shift);
 *  - any event waiting for review ⇒ `base: null` (`ambiguous_base`): a new
 *    capture would either depend on a punch ERPat is about to refuse, or
 *    contradict the queue head the repository checks at commit time
 *    (FE-4 `commitCapture`), so capture waits for ERPat to settle them;
 *  - an unsettled event the snapshot already holds ⇒ `base: null`
 *    (`ambiguous_base`) until its resend settles (`duplicate`, whose original
 *    receipt D13 then skips): re-applying it would double a break pair's taps
 *    or contradict the state, and a base walked around it would not be the one
 *    ERPat resolves — a punch captured on it gets a FINAL `state_conflict`.
 *    The snapshot keeps no trace of some punches ERPat holds (the taps of a
 *    shift it shows closed, every shift before the last closed one), which is
 *    why every unsettled event before a held one is held too, and one the walk
 *    cannot take AFTER a held one is `pending`, not review-blocked: only the
 *    resend can tell.
 * Invariant: `base === null` ⇔ `blocked !== null`, and then every `next` flag
 * is false. `canCapture()` is the one gate for "may this action be captured".
 *
 * C5: the break cap is the policy's `max_break_entries`, which ERPat keeps in
 * 8..64 ({@link normalizeMaxBreakEntries} clamps into that range). It counts
 * break TAPS. A Break Out is offered only while there is room for it AND for
 * the Back-to-Work tap that must close it — ERPat refuses a Break In at the cap
 * (`break_limit`), so an odd cap would otherwise strand the employee on a
 * break. For the default 24 (and every even cap) this is exactly "fewer taps
 * than the cap".
 *
 * Pure: no storage, no network, no Vue; `nowMs` and `localDayKey` are injected.
 */
import type { AttendanceEventType, FinalReceiptV1 } from '@/contracts/offline-attendance-v1';
import { OFFLINE_FUTURE_TOLERANCE_MS } from '@/contracts/offline-attendance-policy';
import type {
  AttendanceProjection,
  CaptureBase,
  ChainEvent,
  ConfirmedSnapshotRecord,
} from '@/types/offline-attendance';

export interface ProjectionInput {
  confirmed: ConfirmedSnapshotRecord | null;
  chain: ChainEvent[];
  /** The policy's `max_break_entries` (see {@link normalizeMaxBreakEntries}). */
  maxBreakEntries: number;
  /** Corrected (server-aligned) now, for "done for today". */
  nowMs: number;
  /** The employee's local calendar day of an instant, e.g. `2026-10-12`. */
  localDayKey: (ms: number) => string;
}

/** C5: ERPat never sends a break cap below 8 … */
export const MAX_BREAK_ENTRIES_MIN = 8;
/** … nor above 64 (its `expected_break_count` ceiling) … */
export const MAX_BREAK_ENTRIES_MAX = 64;
/** … and 24 when the tenant has no setting. */
export const MAX_BREAK_ENTRIES_DEFAULT = 24;

/**
 * The break cap to enforce: a whole number of taps within the contract's
 * 8..64 (C5), or the 24 default when the value is unreadable (absent, NaN,
 * infinite). ERPat computes the same cap; clamping only matters for a policy
 * that broke the contract.
 */
export function normalizeMaxBreakEntries(value: number | null | undefined): number {
  if (typeof value !== 'number' || !Number.isFinite(value)) return MAX_BREAK_ENTRIES_DEFAULT;
  return Math.min(MAX_BREAK_ENTRIES_MAX, Math.max(MAX_BREAK_ENTRIES_MIN, Math.floor(value)));
}

/**
 * Is `confirmed` a confirmed snapshot at all — one the projection walks from,
 * or reports as ambiguous (`open_rows` ≥ 2)? False exactly when
 * {@link project} answers `blocked: 'no_base'` for it, whatever the chain: no
 * record, or one that comes back from storage self-contradicting (an open row
 * without its id or revision, a tap count that disagrees with on/off break, an
 * unreadable `as_of` or tap, a malformed open-row count…).
 *
 * THE validator of a stored snapshot: readiness rule 13 uses it, so readiness
 * never reports `ready` for a snapshot the projection cannot build on.
 * Deliberately NOT a type guard: a `false` can be a non-null, corrupt record.
 */
export function isBaseSnapshot(confirmed: ConfirmedSnapshotRecord | null): boolean {
  return readConfirmed(confirmed).kind !== 'none';
}

/** May `action` be captured now? False whenever there is no base. */
export function canCapture(p: AttendanceProjection, action: AttendanceEventType): boolean {
  if (p.base === null) return false;
  switch (action) {
    case 'time_in':
      return p.next.timeIn;
    case 'time_out':
      return p.next.timeOut;
    case 'break_out':
      return p.next.breakOut;
    case 'break_in':
      return p.next.breakIn;
    default: {
      // Exhaustive over AttendanceEventType (the assignment fails to compile
      // if a type is added); a value outside it — a caller that went through
      // `unknown` — is never capturable.
      const unknownAction: never = action;
      void unknownAction;
      return false;
    }
  }
}

/* ── Internals ─────────────────────────────────────────────────────────── */

type ShiftState = 'off_shift' | 'working' | 'on_break';

interface ClosedShift {
  inMs: number | null;
  outMs: number;
}

/** Where the walk stands: the shift identity, its taps and the last closed shift. */
interface Walk {
  state: ShiftState;
  attendanceId: string | null;
  revision: string | null;
  /** The event id of an UNSETTLED Time In that opened the current shift (D8). */
  localShiftId: string | null;
  shiftInMs: number | null;
  breaks: number[];
  lastClosed: ClosedShift | null;
}

/**
 * The punch times the snapshot holds, in whole seconds (ERPat stores
 * DATETIME seconds, flooring the milliseconds away), by the event type that
 * writes them — what an unsettled event is matched against to tell whether
 * ERPat already committed it (Review Focus 1).
 */
interface SnapshotSlots {
  /** The open row's clock-in and the last closed shift's clock-in. */
  timeIn: number[];
  /** The open row's break taps (a closed shift's taps are not in the snapshot). */
  breaks: number[];
  /** The last closed shift's clock-out. */
  timeOut: number[];
}

/**
 * The attendance ROWS the snapshot shows — what an accepted receipt is matched
 * against to tell whether the snapshot already holds its write although it was
 * read before the receipt's `received_at` (the D13 read race).
 */
interface SnapshotRows {
  /** The single open row's id and revision (null without exactly one open row). */
  openId: string | null;
  openRevision: string | null;
  /** The last closed row's id (null when there is none, or its times cannot be read — C3). */
  closedId: string | null;
}

type Start =
  | { kind: 'none' }
  | { kind: 'ambiguous'; lastClosed: ClosedShift | null; times: number[] }
  | { kind: 'ok'; asOfMs: number; walk: Walk; times: number[]; slots: SnapshotSlots; rows: SnapshotRows };

const NOTHING_NEXT: AttendanceProjection['next'] = {
  timeIn: false,
  timeOut: false,
  breakOut: false,
  breakIn: false,
  breakCapReached: false,
  doneForToday: false,
};

function parseTime(value: string | null | undefined): number | null {
  if (typeof value !== 'string') return null;
  const ms = Date.parse(value);
  return Number.isFinite(ms) ? ms : null;
}

/**
 * C3: a last closed shift whose times cannot be read, or whose Time Out is
 * before its Time In, is no information at all — never a reason to refuse the
 * whole snapshot.
 */
function readLastClosed(confirmed: ConfirmedSnapshotRecord): ClosedShift | null {
  const lastClosed = confirmed.snapshot.last_closed;
  if (lastClosed === null || typeof lastClosed !== 'object') return null;
  const inMs = parseTime(lastClosed.in_time);
  const outMs = parseTime(lastClosed.out_time);
  if (inMs === null || outMs === null || outMs < inMs) return null;
  return { inMs, outMs };
}

function closedTimes(closed: ClosedShift | null): number[] {
  if (closed === null) return [];
  return closed.inMs === null ? [closed.outMs] : [closed.inMs, closed.outMs];
}

/** The last closed row's id — only for a last closed shift `readLastClosed()` accepted. */
function readClosedId(confirmed: ConfirmedSnapshotRecord, closed: ClosedShift | null): string | null {
  if (closed === null) return null;
  const id: unknown = confirmed.snapshot.last_closed?.attendance_id;
  return typeof id === 'string' && id !== '' ? id : null;
}

/** The whole second an instant falls in — what ERPat stores for it. */
function secondOf(ms: number): number {
  return Math.floor(ms / 1000) * 1000;
}

function snapshotSlots(shiftInMs: number | null, breaks: readonly number[], lastClosed: ClosedShift | null): SnapshotSlots {
  const seconds = (times: ReadonlyArray<number | null | undefined>): number[] =>
    times.filter((t): t is number => typeof t === 'number' && Number.isFinite(t)).map(secondOf);
  return {
    timeIn: seconds([shiftInMs, lastClosed?.inMs]),
    breaks: seconds(breaks),
    timeOut: seconds([lastClosed?.outMs]),
  };
}

/**
 * The walk's starting point from the confirmed snapshot — or why there is
 * none. The decoder already enforces these rules on everything ERPat sends;
 * they are re-checked because the record comes back from storage, typed but
 * not proven.
 */
function readConfirmed(confirmed: ConfirmedSnapshotRecord | null): Start {
  // Neither the record nor its snapshot is proven to be an object: a stored
  // value read back as anything else must read as no base, never a TypeError.
  if (typeof confirmed !== 'object' || confirmed === null) return { kind: 'none' };
  const stored: unknown = confirmed.snapshot;
  if (typeof stored !== 'object' || stored === null) return { kind: 'none' };
  const snapshot = confirmed.snapshot;
  const asOfMs = Number.isFinite(confirmed.asOfMs) ? confirmed.asOfMs : parseTime(snapshot.as_of);
  if (asOfMs === null) return { kind: 'none' };
  // The tap list is read for its COUNT (on/off break), so it must be a list.
  if (!Array.isArray(snapshot.breaks)) return { kind: 'none' };

  const lastClosed = readLastClosed(confirmed);
  const closedId = readClosedId(confirmed, lastClosed);
  const openRows = snapshot.open_rows;
  if (!Number.isInteger(openRows) || openRows < 0) return { kind: 'none' };
  if (openRows > 1) return { kind: 'ambiguous', lastClosed, times: closedTimes(lastClosed) };

  if (openRows === 0) {
    const consistent =
      snapshot.state === 'off_shift' &&
      snapshot.attendance_id === null &&
      snapshot.attendance_revision === null &&
      snapshot.in_time === null &&
      snapshot.breaks.length === 0;
    if (!consistent) return { kind: 'none' };
    return {
      kind: 'ok',
      asOfMs,
      times: closedTimes(lastClosed),
      slots: snapshotSlots(null, [], lastClosed),
      rows: { openId: null, openRevision: null, closedId },
      walk: {
        state: 'off_shift',
        attendanceId: null,
        revision: null,
        localShiftId: null,
        shiftInMs: null,
        breaks: [],
        lastClosed,
      },
    };
  }

  // Exactly one open row.
  if (snapshot.state !== 'working' && snapshot.state !== 'on_break') return { kind: 'none' };
  if (snapshot.attendance_id === null || snapshot.attendance_revision === null) return { kind: 'none' };
  const breaks: number[] = [];
  for (const tap of snapshot.breaks) {
    const ms = parseTime(tap);
    // An unreadable tap cannot be dropped: the tap COUNT decides on/off break.
    if (ms === null) return { kind: 'none' };
    breaks.push(ms);
  }
  if ((snapshot.state === 'on_break') !== (breaks.length % 2 === 1)) return { kind: 'none' };
  // C3: an unknown (or unreadable) clock-in only means the shift start is unknown.
  const shiftInMs = parseTime(snapshot.in_time);
  return {
    kind: 'ok',
    asOfMs,
    times: [...(shiftInMs === null ? [] : [shiftInMs]), ...breaks, ...closedTimes(lastClosed)],
    slots: snapshotSlots(shiftInMs, breaks, lastClosed),
    rows: { openId: snapshot.attendance_id, openRevision: snapshot.attendance_revision, closedId },
    walk: {
      state: snapshot.state,
      attendanceId: snapshot.attendance_id,
      revision: snapshot.attendance_revision,
      localShiftId: null,
      shiftInMs,
      breaks,
      lastClosed,
    },
  };
}

/**
 * Apply one event's state change, keeping the shift identity; `null` when the
 * current state cannot take it. The caller sets the identity the event brings.
 */
function transition(walk: Walk, type: AttendanceEventType, atMs: number): Walk | null {
  switch (type) {
    case 'time_in':
      if (walk.state !== 'off_shift') return null;
      return { ...walk, state: 'working', shiftInMs: atMs, breaks: [] };
    case 'break_out':
      if (walk.state !== 'working') return null;
      return { ...walk, state: 'on_break', breaks: [...walk.breaks, atMs] };
    case 'break_in':
      if (walk.state !== 'on_break') return null;
      return { ...walk, state: 'working', breaks: [...walk.breaks, atMs] };
    case 'time_out':
      if (walk.state !== 'working') return null;
      return {
        ...walk,
        state: 'off_shift',
        attendanceId: null,
        revision: null,
        localShiftId: null,
        shiftInMs: null,
        breaks: [],
        lastClosed: { inMs: walk.shiftInMs, outMs: atMs },
      };
    default:
      return null;
  }
}

function isUnsettled(e: ChainEvent): boolean {
  return e.status !== 'final';
}

/** Depends on — or, by chain order, follows — an event that is rejected or waiting for review. */
function isHeldBack(e: ChainEvent, rejected: ReadonlySet<string>, followsReview: boolean): boolean {
  return (
    followsReview ||
    (e.dependsOn !== null && rejected.has(e.dependsOn)) ||
    (e.localShiftId !== null && rejected.has(e.localShiftId))
  );
}

/**
 * Review Focus 1: does the snapshot already hold this UNSETTLED event — ERPat
 * committed it, but its answer was lost (the app was killed, the network
 * dropped), and a snapshot read since shows the punch?
 *
 * It does when the event's time, floored to the second as ERPat stores it,
 * is a punch of the same kind in the snapshot: a Time In → the open row's or
 * the last closed shift's clock-in; a Break Out / Break In → one of the open
 * row's taps; a Time Out → the last closed shift's clock-out. And only if the
 * snapshot can hold it at all: ERPat accepts a capture stamped up to
 * `OFFLINE_FUTURE_TOLERANCE_MS` past its own clock (`future_event`), so an
 * event stamped later than that after the snapshot's `as_of` was not
 * committed when the snapshot was read.
 *
 * A false match (a kiosk punch of the same kind in the very same second) costs
 * a short wait: this event and the unsettled ones before it are shown as
 * pending without being applied, capture waits for the resend, and ERPat's
 * answer to the resend decides.
 */
function heldBySnapshot(e: ChainEvent, slots: SnapshotSlots, asOfMs: number): boolean {
  if (!Number.isFinite(e.occurredAtMs) || e.occurredAtMs > asOfMs + OFFLINE_FUTURE_TOLERANCE_MS) return false;
  const second = secondOf(e.occurredAtMs);
  switch (e.eventType) {
    case 'time_in':
      return slots.timeIn.includes(second);
    case 'break_out':
    case 'break_in':
      return slots.breaks.includes(second);
    case 'time_out':
      return slots.timeOut.includes(second);
    default:
      return false;
  }
}

/**
 * The position (in device order) of the LAST unsettled event the snapshot
 * holds (`heldBySnapshot()`), or -1.
 *
 * ERPat commits one chain in order and refuses everything after a refused
 * event (`dependency_rejected`), so when it committed that event it committed
 * every unsettled event before it too — including the ones the snapshot keeps
 * no trace of: the taps of a shift it shows closed, a shift before the last
 * closed one. The walk therefore treats every unsettled event up to that
 * position as held.
 *
 * Events the walk sends to review cannot be held: one depending on (or
 * following) a refused punch, and one whose time cannot be read — and, as in
 * the walk, nothing after such an event counts.
 */
function lastHeldIndex(chain: readonly ChainEvent[], slots: SnapshotSlots, asOfMs: number): number {
  const rejected = new Set<string>();
  let followsReview = false;
  let last = -1;
  for (const [i, e] of chain.entries()) {
    if (!isUnsettled(e)) {
      if (e.outcome === 'rejected') rejected.add(e.clientEventId);
      continue;
    }
    if (isHeldBack(e, rejected, followsReview) || !Number.isFinite(e.occurredAtMs)) {
      rejected.add(e.clientEventId);
      followsReview = true;
      continue;
    }
    if (heldBySnapshot(e, slots, asOfMs)) last = i;
  }
  return last;
}

/**
 * D13, defended against the read race: does the snapshot provably hold this
 * ACCEPTED event's write, although its receipt says ERPat received it after
 * the snapshot's `as_of`? (ERPat may sample `as_of` before reading the rows,
 * so a write committed in between is in the rows but "newer" than `as_of`.)
 *
 * It does when the snapshot shows the row the write produced:
 *  - a Time In created its row — the snapshot shows that row at all, open or
 *    as the last closed one;
 *  - a Break Out / Break In — the open row is that row at exactly the
 *    receipt's revision (ERPat's revision hashes the row's clock state), or
 *    that row is the last closed one (it was open when ERPat accepted the tap,
 *    so a snapshot showing it closed was read after the tap);
 *  - a Time Out — that row is the last closed one.
 * Only an acceptance of this very event counts; any other receipt proves
 * nothing (and is judged by the walk as before).
 */
function snapshotShowsAccepted(e: ChainEvent, rows: SnapshotRows): boolean {
  const receipt = e.receipt;
  if (receipt === null || typeof receipt !== 'object') return false;
  if (receipt.outcome !== 'accepted' || receipt.event_type !== e.eventType) return false;
  const rowId: unknown = receipt.attendance_id;
  if (typeof rowId !== 'string' || rowId === '') return false;
  const shownClosed = rows.closedId === rowId;
  switch (e.eventType) {
    case 'time_in':
      return shownClosed || rows.openId === rowId;
    case 'break_out':
    case 'break_in':
      return (
        shownClosed ||
        (rows.openId === rowId && rows.openRevision !== null && rows.openRevision === receipt.attendance_revision)
      );
    case 'time_out':
      return shownClosed;
    default:
      return false;
  }
}

/**
 * The position (in device order) of the LAST accepted event the snapshot
 * provably holds (`snapshotShowsAccepted()`), or -1. ERPat commits one chain
 * in order, so a snapshot holding that write holds every accepted write before
 * it — the walk skips them all as reflected, whatever their `received_at`.
 */
function lastShownAcceptedIndex(chain: readonly ChainEvent[], rows: SnapshotRows): number {
  let last = -1;
  for (const [i, e] of chain.entries()) {
    if (e.status === 'final' && e.outcome === 'accepted' && snapshotShowsAccepted(e, rows)) last = i;
  }
  return last;
}

/**
 * Classify the chain without a base: unsettled events are pending unless
 * they depend on (or follow) a rejected / review-blocked one, or have no
 * readable time — the walk's own review rules, so the same punch never flips
 * between "Pending" and "Needs review" when a base comes or goes. Nothing is
 * applied, so nothing changes state.
 */
function classifyOnly(chain: readonly ChainEvent[]): { pending: ChainEvent[]; reviewBlocked: ChainEvent[] } {
  const rejected = new Set<string>();
  const pending: ChainEvent[] = [];
  const reviewBlocked: ChainEvent[] = [];
  for (const e of chain) {
    if (!isUnsettled(e)) {
      if (e.outcome === 'rejected') rejected.add(e.clientEventId);
      continue;
    }
    if (isHeldBack(e, rejected, reviewBlocked.length > 0) || !Number.isFinite(e.occurredAtMs)) {
      reviewBlocked.push(e);
      rejected.add(e.clientEventId);
    } else {
      pending.push(e);
    }
  }
  return { pending, reviewBlocked };
}

function maxTime(times: readonly number[]): number | null {
  let latest: number | null = null;
  for (const t of times) {
    if (Number.isFinite(t) && (latest === null || t > latest)) latest = t;
  }
  return latest;
}

/** A projection that offers nothing: no base, or one the chain cannot be reconciled with. */
function unbased(
  chain: ChainEvent[],
  blocked: 'no_base' | 'ambiguous_base',
  basis: 'none' | 'confirmed',
  lastClosed: ClosedShift | null,
  times: readonly number[],
): AttendanceProjection {
  const { pending, reviewBlocked } = classifyOnly(chain);
  return {
    basis,
    state: 'unknown',
    shiftInTimeMs: null,
    breakTimesMs: [],
    lastClosedOutMs: lastClosed?.outMs ?? null,
    lastClosedInMs: lastClosed?.inMs ?? null,
    blocked,
    next: { ...NOTHING_NEXT },
    chain,
    pending,
    reviewBlocked,
    lastEventAtMs: maxTime(times),
    base: null,
  };
}

/**
 * True when an accepted receipt cannot be applied on top of the walk: it is
 * not an acceptance of THIS event's action, names no row/revision, or acts on
 * a row other than the open one.
 */
function receiptConflict(walk: Walk, e: ChainEvent, receipt: FinalReceiptV1): boolean {
  if (receipt.outcome !== 'accepted' || receipt.event_type !== e.eventType) return true;
  if (receipt.attendance_id === null || receipt.attendance_revision === null) return true;
  // Every action but Time In acts on the open row: the receipt must name it.
  return e.eventType !== 'time_in' && walk.attendanceId !== null && walk.attendanceId !== receipt.attendance_id;
}

/* ── The reducer ───────────────────────────────────────────────────────── */

export function project(input: ProjectionInput): AttendanceProjection {
  // A sorted COPY: the caller's array (and its events) are never touched.
  const chain = [...input.chain].sort((a, b) => a.deviceSeq - b.deviceSeq);
  const cap = normalizeMaxBreakEntries(input.maxBreakEntries);

  const start = readConfirmed(input.confirmed);
  if (start.kind === 'none') return unbased(chain, 'no_base', 'none', null, []);
  if (start.kind === 'ambiguous') return unbased(chain, 'ambiguous_base', 'confirmed', start.lastClosed, start.times);
  const ambiguous = (): AttendanceProjection =>
    unbased(chain, 'ambiguous_base', 'confirmed', start.walk.lastClosed, start.times);

  let walk = start.walk;
  const rejected = new Set<string>();
  const pending: ChainEvent[] = [];
  const reviewBlocked: ChainEvent[] = [];
  /** The confirmed punches, the events applied on top, and the events the snapshot holds. */
  const knownTimes: number[] = [...start.times];
  let unsettledSeen = false;
  /** An unsettled event changed the walked state (`basis: 'projected'`). */
  let appliedProvisionally = false;
  /** An unsettled event the snapshot already holds is waiting for its resend to settle. */
  let heldSeen = false;
  /** Accepted events up to here are in the snapshot whatever their `received_at`. */
  const lastShown = lastShownAcceptedIndex(chain, start.rows);
  /** Unsettled events up to here are held by the snapshot (Review Focus 1). */
  const lastHeld = lastHeldIndex(chain, start.slots, start.asOfMs);

  for (const [i, e] of chain.entries()) {
    if (!isUnsettled(e)) {
      if (e.outcome === 'rejected') {
        rejected.add(e.clientEventId);
        continue;
      }
      // A final row is accepted or rejected; anything else cannot be placed.
      if (e.outcome !== 'accepted') return ambiguous();
      // ERPat settles one chain in order: an acceptance AFTER an event the
      // device still holds as unsettled means the server state already
      // contains effects the walk would apply again — wait for the resend.
      if (unsettledSeen) return ambiguous();
      // The snapshot shows this write, or a later one of the chain: reflected,
      // even if it was received after `as_of` (the read raced the commit).
      if (i <= lastShown) continue;

      const receipt = e.receipt;
      if (receipt === null) {
        // A receipt only goes away with pruning, which waits until a snapshot
        // reflects the event — so one that happened before this snapshot was
        // read is taken as reflected. One that happened AFTER it cannot be in
        // the snapshot, and without the receipt there is no row or revision
        // to apply it with.
        if (Number.isFinite(e.occurredAtMs) && e.occurredAtMs <= start.asOfMs) continue;
        return ambiguous();
      }
      const receivedMs = parseTime(receipt.received_at);
      if (receivedMs === null) return ambiguous();
      if (receivedMs <= start.asOfMs) continue; // D13: the snapshot reflects it.
      if (!Number.isFinite(e.occurredAtMs) || receiptConflict(walk, e, receipt)) return ambiguous();

      const next = transition(walk, e.eventType, e.occurredAtMs);
      if (next === null) return ambiguous();
      walk =
        next.state === 'off_shift'
          ? next
          : { ...next, attendanceId: receipt.attendance_id, revision: receipt.attendance_revision, localShiftId: null };
      knownTimes.push(e.occurredAtMs);
      continue;
    }

    // Unsettled (pending or in flight).
    unsettledSeen = true;
    if (isHeldBack(e, rejected, reviewBlocked.length > 0) || !Number.isFinite(e.occurredAtMs)) {
      // Depends on (or follows) a punch that is refused or waiting for
      // review — or cannot be placed on the shift at all (no readable time).
      reviewBlocked.push(e);
      rejected.add(e.clientEventId);
      continue;
    }
    if (i <= lastHeld) {
      // Review Focus 1: ERPat committed it — the snapshot shows this punch, or
      // a later one of the chain — but the receipt never arrived. Applying it
      // again would double it (a break pair adds two more taps), contradict the
      // state (a second Time In), or replay a shift the snapshot already moved
      // past; either way the base would no longer be what ERPat holds, and a
      // punch captured on it would be refused as `state_conflict`. It waits —
      // not for review — and so does capture, until the resend settles it
      // (`duplicate`, with the original receipt, which D13 then skips).
      heldSeen = true;
      pending.push(e);
      knownTimes.push(e.occurredAtMs);
      continue;
    }
    const next = transition(walk, e.eventType, e.occurredAtMs);
    if (next === null) {
      if (heldSeen) {
        // After a punch ERPat holds, it may hold this one too where the
        // snapshot keeps no trace of it. Its resend decides; until then it is
        // waiting, not refused.
        pending.push(e);
        continue;
      }
      reviewBlocked.push(e);
      rejected.add(e.clientEventId);
      continue;
    }
    walk =
      e.eventType === 'time_in'
        ? { ...next, attendanceId: null, revision: null, localShiftId: e.clientEventId }
        : next;
    pending.push(e);
    appliedProvisionally = true;
    knownTimes.push(e.occurredAtMs);
  }

  const lastClosed = walk.lastClosed;
  const doneForToday =
    walk.state === 'off_shift' &&
    lastClosed !== null &&
    lastClosed.inMs !== null &&
    Number.isFinite(input.nowMs) &&
    input.localDayKey(lastClosed.inMs) === input.localDayKey(input.nowMs);
  // Room for a Break Out AND the Break In that closes it (see the module note on C5).
  const breakRoom = walk.breaks.length + 2 <= cap;

  // A punch waiting for review, or one ERPat holds without a receipt yet,
  // leaves no base a new capture could safely be frozen with.
  const capturable = reviewBlocked.length === 0 && !heldSeen;
  const next: AttendanceProjection['next'] = capturable
    ? {
        timeIn: walk.state === 'off_shift' && !doneForToday,
        timeOut: walk.state === 'working',
        breakOut: walk.state === 'working' && breakRoom,
        breakIn: walk.state === 'on_break',
        breakCapReached: walk.state === 'working' && !breakRoom,
        doneForToday,
      }
    : { ...NOTHING_NEXT };
  const lastPending = pending[pending.length - 1];
  const base: CaptureBase | null = capturable
    ? {
        attendanceId: walk.attendanceId,
        attendanceRevision: walk.revision,
        localShiftId: walk.localShiftId,
        expectedState: walk.state,
        expectedBreakCount: walk.breaks.length,
        dependsOn: lastPending === undefined ? null : lastPending.clientEventId,
      }
    : null;

  return {
    // `projected` only when a pending event changed what is shown; events the
    // snapshot already holds leave ERPat's own state on screen.
    basis: appliedProvisionally ? 'projected' : 'confirmed',
    state: walk.state,
    shiftInTimeMs: walk.state === 'off_shift' ? null : walk.shiftInMs,
    breakTimesMs: walk.state === 'off_shift' ? [] : [...walk.breaks],
    lastClosedOutMs: lastClosed?.outMs ?? null,
    lastClosedInMs: lastClosed?.inMs ?? null,
    blocked: capturable ? null : 'ambiguous_base',
    next,
    chain,
    pending,
    reviewBlocked,
    lastEventAtMs: maxTime(knownTimes),
    base,
  };
}
